import { IScope, ITimeoutService, IWindowService, IPromise, IQService, noop } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { IInsight, IUpdateInsight, IInsightsQueryParams, InsightService, IInsightUsage } from "@gtmhub/insights";
import { IUsersIdMap } from "@gtmhub/users/models";
import { UserService } from "@gtmhub/users/userService";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IUserItemAllowedActions } from "@gtmhub/sessions/models/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { IInsightBoardItemResponse, InsightboardService } from "@gtmhub/insightboards";
import { IGoal } from "@gtmhub/goals/models";
import { IInsightboard, IInsightboardItem } from "../models/models";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { IStateInit } from "@gtmhub/core/routing";
import { MetricService, IMetric } from "@gtmhub/okrs/metrics";
import { IKpi, KpiService } from "@gtmhub/kpis";
import { AssigneeService } from "@gtmhub/assignees/services";
import { IDropdownButtonMenuItem } from "@gtmhub/shared/components";
import { IdMap } from "@gtmhub/util";

export interface IInsightboardWidgetBrowserParams {
  widgetsDashboardId: string;
}

interface IWidgetBrowserPagginationScope {
  totalCount: number;
  startIndex: number;
  endIndex: number;
  currentPage: number;
  onPageChange(page: number): void;
}

export interface IInsightboardWidgetBrowserScope extends IScope, IWidgetBrowserPagginationScope {
  insights: IInsight[];
  indicators: {
    loading: boolean;
    saving: boolean;
    loadingInsights: boolean;
  };
  permissions: {
    browser: boolean;
  };
  usersIdMap: IUsersIdMap;
  singleBoard: boolean;
  totalPages: number;
  resultsOnPageCount: number;
  selectedInsight: IInsight;
  getSelectedInsight(insight: IInsight): void;
  cancelMultipleWidgets(): void;
  filterInsights(element: any): void;
  createInsight(isCodelessInsight?: boolean): void;
  usagePlacesCount(insight: IInsight): number;
  clone(insight: IInsight): void;
  confirmDelete(insight: IInsight): void;
  editInsight(insightName: string, isCodeless: boolean): void;
  editInsightDetails(insight: IInsight): void;
  getSomeInsights(insightName?: string, indicatorName?: string): void;
  clearInsightSearchField(): void;
  generateSubmitToMarketplaceEmail(insightName: string): void;

  selectedInsightsCount: number;
  dropdownMenuItems: IDropdownButtonMenuItem[];
  selectedInsightsMap: IdMap<IInsight>;
  selectInsight(insight: IInsight): void;
  removeSelectedInsights(): void;
  addSelectedInsights(map: IdMap<IInsight>);
  viewUsage(insight: IInsight): void;
  insightUsageMap: IdMap<IInsightUsage>;
}

interface EditInsightDetailsScope extends IScope {
  indicators: { saving: boolean };
  insight: IUpdateInsight;
  submit(): void;
}

export class InsightboardWidgetBrowserCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$uibModal",
    "$timeout",
    "$window",
    "$q",
    "InsightService",
    "UserService",
    "UserProfileService",
    "AccountResolverService",
    "PermissionsService",
    "AnalyticsService",
    "UIErrorHandlingService",
    "AssigneeService",
    "$ngRedux",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IInsightboardWidgetBrowserScope,
    private $state: StateService,
    private $stateParams: IInsightboardWidgetBrowserParams,
    private $uibModal: IModalService,
    private $timeout: ITimeoutService,
    private $window: IWindowService,
    private $q: IQService,
    private insightService: InsightService,
    private userService: UserService,
    private profileService: UserProfileService,
    private accountResolverService: AccountResolverService,
    private permissionsService: PermissionsService,
    private analyticsService: AnalyticsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeService: AssigneeService
  ) {
    const dashboardId = this.$stateParams.widgetsDashboardId;
    this.$scope.dropdownMenuItems = [
      {
        textKey: "add_template",
        descriptionKey: "get_free_ready_to_use_insights",
        action: { href: `/marketplace/insights?currentPage=1&dashboardId=${dashboardId}`, target: "_blank" },
        externalLink: true,
        e2eData: "linkToMarketplace",
        imageSrc: require("wwwroot/img/icons/marketplace.svg"),
      },
      {
        action: { handler: () => this.createInsight(true) },
        textKey: "create_insight",
        descriptionKey: "build_your_custom_insight",
        e2eData: "createInsightBtn",
        imageSrc: require("wwwroot/img/icons/insight2.svg"),
      },
    ];
  }

  private insightsFields = "id,createdById,modifiedById,dateCreated,dateUpdated,formatting,description,direct,name,title,usedFilters,size{height,width},source,state";
  private lastSearchedInsightName: string;
  private insightsPerPage = 12;

  private indicatorNames = {
    loading: "loading",
    saving: "saving",
    loadingInsights: "loadingInsights",
  };

  private strings = {
    insight: "gtmhub.insight",
    insightCodelessEditor: "gtmhub.insightCodelessEditor",
  };

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: false,
      saving: false,
      loadingInsights: false,
    };

    this.$scope.currentPage = 1;
    this.$scope.onPageChange = this.onPageChange;
    this.calculatePagginationIndexes(this.$scope.currentPage);

    this.$scope.selectedInsightsCount = 0;
    this.$scope.selectedInsightsMap = {};
    this.$scope.selectInsight = this.selectInsight;
    this.$scope.removeSelectedInsights = this.removeSelectedInsights;
    this.$scope.addSelectedInsights = this.addSelectedInsights;

    this.$scope.filterInsights = this.filterInsights;
    this.$scope.usagePlacesCount = this.usagePlacesCount;
    this.$scope.clone = this.clone;
    this.$scope.confirmDelete = this.confirmDelete;
    this.$scope.editInsight = this.editInsight;
    this.$scope.createInsight = this.createInsight;
    this.$scope.editInsightDetails = this.editInsightDetails;
    this.$scope.clearInsightSearchField = this.clearInsightSearchField;
    this.$scope.getSomeInsights = this.getSomeInsights;
    this.$scope.generateSubmitToMarketplaceEmail = this.generateSubmitToMarketplaceEmail;
    this.$scope.getSelectedInsight = this.getSelectedInsight;
    this.$scope.viewUsage = this.viewUsages;
    this.$scope.insightUsageMap = {};

    const promises: IPromise<unknown>[] = [];

    if (this.$stateParams.widgetsDashboardId) {
      this.$scope.singleBoard = true;
      this.$scope.indicators.loading = true;

      promises.push(
        this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$stateParams.widgetsDashboardId, "insightBoard").then(
          (actions: IUserItemAllowedActions) => {
            this.$scope.permissions = {
              browser: actions.currentUserActions.indexOf("update") !== -1 && this.$rootScope.hasPermission("ManageData"),
            };
            return this.getUsersAndInsightsInit();
          },
          (err) => this.handleIndicatorError(err, "loading")
        )
      );
    } else {
      this.$scope.singleBoard = false;

      this.$scope.permissions = {
        browser: this.$rootScope.hasPermission("ManageData"),
      };
      promises.push(this.getUsersAndInsightsInit());
    }

    return this.$q.all(promises);
  };

  private clearInsightSearchField = (): void => {
    this.$scope.currentPage = 1;
    this.$scope.indicators.loadingInsights = true;
    this.lastSearchedInsightName = null;
    this.getSomeInsights(null, this.indicatorNames.loadingInsights);
  };

  private getUsersAndInsightsInit = (): IPromise<unknown> => {
    this.$scope.indicators.loading = true;

    return this.userService.getUsersIdMap().then(
      (users) => {
        this.$scope.usersIdMap = users;
        this.getSomeInsights(null, this.indicatorNames.loading);
      },
      (err) => this.handleIndicatorError(err, "loadingUsers")
    );
  };

  private getSelectedInsight = (insight: IInsight): void => {
    this.insightService.getInsightV2(insight.name).then(
      (insight) => {
        this.$scope.selectedInsight = insight;
        this.enrichWithUsage(insight.id);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private getSkip = (): number => {
    return (this.$scope.currentPage - 1) * this.insightsPerPage;
  };

  private onPageChange = (page: number): void => {
    this.$scope.currentPage = page;
    this.$scope.indicators.loadingInsights = true;
    this.calculatePagginationIndexes(page);

    this.getSomeInsights(this.lastSearchedInsightName, this.indicatorNames.loadingInsights);
  };

  private getSomeInsights = (insightName?: string, indicatorName?: string): void => {
    if (this.$scope.permissions.browser) {
      if (insightName && insightName !== this.lastSearchedInsightName) {
        this.$scope.currentPage = 1;
        this.$scope.indicators[indicatorName] = true;
      }

      const queryParams: IInsightsQueryParams = {
        skip: this.getSkip(),
        fields: this.insightsFields,
        take: this.insightsPerPage,
        stringSearch: insightName || null,
      };

      this.insightService.getInsightsV2(queryParams).then(
        (insights: ICollection<IInsight>) => {
          this.lastSearchedInsightName = insightName;

          this.$scope.totalPages = Math.ceil(insights.totalCount / this.insightsPerPage);
          this.$scope.resultsOnPageCount = insights.items.length;
          this.$scope.totalCount = insights.totalCount;
          this.$scope.insights = this.enrichInsights(insights.items);

          this.$scope.indicators[indicatorName] = false;

          this.setAutofocus("focusTextInput");
          this.calculatePagginationIndexes(this.$scope.currentPage);
        },
        (err) => this.handleIndicatorError(err, indicatorName, indicatorName === this.indicatorNames.loadingInsights)
      );
    } else {
      this.$scope.indicators.loading = false;
      this.$scope.indicators.loadingInsights = false;
      this.setAutofocus("focusTextInput");
    }
  };

  private usagePlacesCount = (insight: IInsight): number => {
    const insightUsage = this.$scope.insightUsageMap[insight.id];
    if (insightUsage) {
      return insightUsage.insightboards?.length + insightUsage.kpis?.length + insightUsage.metrics?.length;
    }
  };

  private enrichInsights = (insights: IInsight[]): IInsight[] => {
    insights.forEach((insight) => {
      if (this.$scope.usersIdMap[insight.createdById]) {
        const user = angular.copy(this.$scope.usersIdMap[insight.createdById]);

        if (user) {
          user.name = `${user.firstName} ${user.lastName}`;
        }

        insight.createdBy = user;
      }
      if (this.$scope.usersIdMap[insight.modifiedById]) {
        const user = angular.copy(this.$scope.usersIdMap[insight.modifiedById]);

        if (user) {
          user.name = `${user.firstName} ${user.lastName}`;
        }

        insight.modifiedBy = user;
      }
    });

    return insights;
  };

  private createInsight = (isCodelessInsight?: boolean): void => {
    // There is a problem with the modals. We need to use the `modal` util, but
    // it requires a bigger refactoring in insights.states.ts definitions
    this.$state
      .go("^.close")
      .catch(noop)
      .then(() => {
        this.$uibModal.open({
          templateUrl: require("../../insights/views/insight-form.jade"),
          windowClass: "medium-small",
          controller: "CreateInsightCtrl",
          resolve: {
            isCodelessInsight: (): boolean => {
              return isCodelessInsight;
            },
          },
        });
      });
  };

  private clone = (insight: IInsight): void => {
    let selectedIndex: number;

    for (let i = 0; i < this.$scope.insights.length; i++) {
      if (this.$scope.insights[i].name === insight.name) {
        selectedIndex = i;

        break;
      }
    }

    this.$scope.indicators[insight.id] = true;

    this.insightService.getInsightV2(insight.name).then(
      (freshInsight) => {
        freshInsight.name = freshInsight.name + "-" + Math.random().toString(36).substr(2, 5);
        freshInsight.title = freshInsight.title + " cloning";
        // check if we have deactivated owners and remove them
        freshInsight.createdById = this.assigneeService.getActiveAssigneesOnly(Array.of(freshInsight.createdById)).toString();

        if (!freshInsight.language) {
          freshInsight.language = "r";
        }

        this.insightService.createInsight(freshInsight, { cloned: true }).then(
          (newinsight) => {
            this.$scope.indicators[insight.id] = false;

            this.$scope.insights.splice(selectedIndex + 1, 0, newinsight);
          },
          (err) => this.handleIndicatorError(err, "[insight.id]", true)
        );
      },
      (err) => this.handleIndicatorError(err, "[insight.id]", true)
    );
  };

  private getUsageOnKpis = (insight: IInsight, $scope): void => {
    this.insightService.getUsageOnKpis(insight.id).then(
      (kpiUsage) => {
        $scope.loadingKpiUsage = false;
        $scope.kpiUsage = kpiUsage;
      },
      (err) => {
        $scope.loadingKpiUsage = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  };

  private goToKpi = (kpi: IKpi, $uibModalInstance: IModalServiceInstance): void => {
    $uibModalInstance.close();
    this.$state.go("gtmhub.kpis._singleKpi.form", { kpiId: kpi.id });
  };

  private confirmDelete = (insight: IInsight) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../insights/views/in-use.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "InsightService",
        "MetricService",
        "GoalService",
        "$uibModalInstance",
        "InsightboardService",
        "KpiService",
        function (
          $scope,
          insightService: InsightService,
          metricService: MetricService,
          goalService: GoalService,
          $uibModalInstance: IModalServiceInstance,
          insightboardService: InsightboardService,
          kpiService: KpiService
        ) {
          $scope.insight = insight;
          $scope.indicators = { deleting: false };

          $scope.loadingMetricUsage = true;

          insightService.getUsageInMetrics(insight.id).then(
            (usage) => {
              $scope.loadingMetricUsage = false;

              $scope.usage = usage;
            },
            (err) => {
              $scope.loadingMetricUsage = false;

              ctrl.uiErrorHandlingService.handleModal(err);
            }
          );

          $scope.loadingBoardUsage = true;

          insightService.getUsageOnBoards(insight.id).then(
            (boardUsage) => {
              $scope.loadingBoardUsage = false;

              $scope.boardUsage = boardUsage;
            },
            (err) => {
              $scope.loadingBoardUsage = false;

              ctrl.uiErrorHandlingService.handleModal(err);
            }
          );

          $scope.loadingKpiUsage = true;

          ctrl.getUsageOnKpis(insight, $scope);

          $scope.goToGoal = (metric: IMetric) => {
            $uibModalInstance.close();

            ctrl.$state.go("gtmhub.goals.all._goalSidebar.goal._metric.metric", { id: metric.goalId, planningSessionId: metric.sessionId, metricId: metric.id });
          };

          $scope.goToBoard = (board: IInsightboard) => {
            $uibModalInstance.close();
            ctrl.$state.go("gtmhub.insightboard", { dashboardId: board.id });
          };

          $scope.goToKpi = (kpi: IKpi) => ctrl.goToKpi(kpi, $uibModalInstance);

          $scope.removeKpi = (kpi: IKpi) => {
            kpiService.patchKpi(kpi.id, { insight: null }).then(
              () => {
                $scope.kpiUsage = $scope.kpiUsage.filter((usageKpi) => usageKpi.id !== kpi.id);
              },
              (err) => ctrl.handleIndicatorError(err, "patching", true)
            );
          };

          $scope.delete = () => {
            $scope.indicators.deleting = true;

            insight.processing = true;

            ctrl.insightService.deleteInsight(insight.name).then(
              () => {
                insight.processing = "deleted";

                ctrl.$timeout(() => {
                  ctrl.$scope.insights = ctrl.$scope.insights.filter((currentInsight: IInsight): boolean => {
                    return currentInsight.name !== insight.name;
                  });

                  $scope.indicators.deleting = false;
                });

                $uibModalInstance.close();
              },
              (err) => {
                insight.processing = false;
                ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "deleting", inline: true });
              }
            );
          };

          $scope.removeInsight = (usedBoard: IInsightboard) => {
            usedBoard.deleting = true;

            insightboardService
              .getInsightboard(usedBoard.id)
              .then((insightboard: IInsightboard) => {
                let removedWidgets: IInsightboardItem[];

                for (let i = 0; i < insightboard.insights.length; i++) {
                  if (insightboard.insights[i].insightId === $scope.insight.id) {
                    removedWidgets = insightboard.insights.splice(i, 1);

                    break;
                  }
                }

                return insightboardService
                  .saveInsightboardItems(insightboard.id, [...insightboard.insights, ...insightboard.sectionHeaders])
                  .then((savedWidgets: IInsightBoardItemResponse) => {
                    for (let i = 0; i < $scope.boardUsage.length; i++) {
                      if ($scope.boardUsage[i].tag === insightboard.tag) {
                        $scope.boardUsage[i].deleting = false;

                        $scope.boardUsage.splice(i, 1);

                        ctrl.$rootScope.$broadcast("insightRemoved", removedWidgets[0], [...savedWidgets.insights, ...savedWidgets.sectionHeaders]);
                      }
                    }
                  });
              })
              .catch((err) => {
                usedBoard.deleting = false;

                ctrl.uiErrorHandlingService.handleModal(err);
              });
          };

          $scope.deleteMetric = (metric: IMetric) => {
            metric.deleting = true;

            goalService
              .getGoal(metric.goalId)
              .then((goal: IGoal) => {
                for (let i = 0; i < goal.metrics.length; i++) {
                  if (goal.metrics[i].id === metric.id) {
                    goal.metrics.splice(i, 1);

                    break;
                  }
                }

                goal.metricsCount = goal.metrics.length;

                return metricService.deleteMetric(metric.goalId, metric.id).then(() => {
                  for (let i = 0; i < $scope.usage.length; i++) {
                    if ($scope.usage[i].id === metric.id) {
                      $scope.usage[i].deleting = false;

                      $scope.usage.splice(i, 1);

                      break;
                    }
                  }
                });
              })
              .catch((err) => {
                metric.deleting = false;

                ctrl.uiErrorHandlingService.handleModal(err);
              });
          };
        },
      ],
    });
  };

  private editInsight = (insightName: string, isCodeless: boolean): void => {
    const options = {
      name: insightName,
      dashboardId: this.$stateParams.widgetsDashboardId || null,
    };

    if (isCodeless) {
      this.$state.go(this.strings.insightCodelessEditor, options);
      return;
    }

    this.$state.go(this.strings.insight, options);
  };

  private editInsightDetails = (insight: IInsight): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../insights/views/insight-form.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        "InsightService",
        function ($scope: EditInsightDetailsScope, $uibModalInstance: IModalServiceInstance, insightService: InsightService) {
          $scope.insight = {
            id: insight.id,
            title: insight.title,
            description: insight.description,
            size: {
              height: insight.size ? insight.size.height : null,
              width: insight.size ? insight.size.width : null,
            },
            direct: insight.direct,
          };

          $scope.indicators = ctrl.$scope.indicators;

          $scope.submit = () => {
            const insightFieldsToUpdate: IUpdateInsight = ctrl.insightService.getInsightFieldsToUpdate($scope.insight, insight);

            // if there are fields to update besides the id => patch, else don't
            if (Object.keys(insightFieldsToUpdate).length > 1) {
              $scope.indicators.saving = true;
              insightService.patchInsight(insightFieldsToUpdate).then(
                () => {
                  $scope.indicators.saving = false;

                  $uibModalInstance.close();

                  insight.title = $scope.insight.title;
                  insight.description = $scope.insight.description;
                  insight.size = $scope.insight.size;
                  insight.direct = $scope.insight.direct;
                  ctrl.getSomeInsights(ctrl.lastSearchedInsightName ? ctrl.lastSearchedInsightName : null, ctrl.indicatorNames.loading);
                },
                (err) => ctrl.handleIndicatorError(err, "saving", true)
              );
            } else {
              $uibModalInstance.close();
            }
          };
        },
      ],
    });
  };

  // client-side filter for long list filtering by insight title
  private filterInsights = (element) => {
    return !!element.name.match(/^Ma/);
  };

  private trackInsightSubmitClick = (insightId: string): void => {
    this.analyticsService.track("Submit insight to marketplace link clicked", {
      account_name: this.accountResolverService.getAccountName(),
      insight_id: insightId,
    });
  };

  private generateSubmitToMarketplaceEmail = (insightName: string): void => {
    this.insightService.getInsightV2(insightName).then(
      (insight) => {
        this.trackInsightSubmitClick(insight.id);
        const submitInsightEmailContent = this.insightService.generateSubmitInsightEmail(insight, this.profileService.getProfile());

        this.$window.open(
          "mailto:" + submitInsightEmailContent.mailto + "?subject=" + submitInsightEmailContent.subject + "&body=" + submitInsightEmailContent.body,
          "_self"
        );
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private setAutofocus = (eventName: string) => {
    this.$scope.$broadcast(eventName);
  };

  private addSelectedInsights = (selectedInsightMap: IdMap<IInsight>): void => {
    if (!this.$stateParams.widgetsDashboardId) {
      this.openInsightboardSelectorModal(selectedInsightMap);
      return;
    }

    const selectedInsightIds = Object.keys(selectedInsightMap).join(",");
    this.insightService.getInsights(null, selectedInsightIds).then(
      // eslint-disable-next-line sonarjs/no-identical-functions
      (insights) => {
        this.$state.go("gtmhub.insightboard.widgets.done", {
          widgets: insights.items,
        });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private selectInsight = (insight: IInsight): void => {
    const selectedInsight = this.$scope.selectedInsightsMap[insight.id];
    if (!selectedInsight) {
      this.$scope.selectedInsightsMap[insight.id] = insight;
      this.$scope.selectedInsightsCount++;
    } else {
      delete this.$scope.selectedInsightsMap[insight.id];
      this.$scope.selectedInsightsCount--;
    }
  };

  private removeSelectedInsights = (): void => {
    this.$scope.selectedInsightsMap = {};
    this.$scope.selectedInsightsCount = 0;
  };

  private openInsightboardSelectorModal = (selectedInsightMap: IdMap<IInsight>): void => {
    this.$uibModal.open({
      templateUrl: require("../../insights/views/insight-form.jade"),
      component: "insightDashboardSelector",
      windowClass: "insight-dashboard-selector-modal medium-small",
      resolve: {
        selectedInsights: (): IInsight[] => Object.values(selectedInsightMap),
      },
    });
  };
  private viewUsages = (insight: IInsight): void => {
    const insightUsage = this.$scope.insightUsageMap[insight.id];

    this.$uibModal.open({
      windowClass: "medium",
      component: "viewInsightUsage",
      resolve: {
        insightUsage: () => insightUsage,
        insightName: () => insight.name,
        onInsightDeleted: () => this.onInsightDeleted,
      },
    });
  };

  private onInsightDeleted = (insightName): void => {
    this.$scope.insights = this.$scope.insights.filter((insight) => insight.name != insightName);
  };

  private enrichWithUsage = (insightId: string): void => {
    delete this.$scope.insightUsageMap[insightId];
    const boardsPromise = this.insightService.getUsageOnBoards(insightId);
    const kpisPromise = this.insightService.getUsageOnKpis(insightId);
    const metricPromise = this.insightService.getUsageInMetrics(insightId);

    this.$q
      .all({ boards: boardsPromise, kpis: kpisPromise, metrics: metricPromise })
      .then(({ boards, kpis, metrics }) => {
        const usage: IInsightUsage = {
          insightboards: boards,
          kpis: kpis,
          metrics: metrics,
        };
        this.$scope.insightUsageMap[insightId] = usage;
      })
      .catch((error) => {
        delete this.$scope.insightUsageMap[insightId];
        this.uiErrorHandlingService.handleModal(error);
      });
  };

  private calculatePagginationIndexes = (currentPage: number): void => {
    const startIndex = currentPage == 1 ? 1 : (currentPage - 1) * this.insightsPerPage;
    let endIndex = 1;
    const lastPage = Math.ceil(this.$scope.totalCount / this.insightsPerPage);
    if (currentPage == lastPage) {
      endIndex = this.$scope.totalCount;
    } else {
      endIndex = currentPage == 1 ? this.insightsPerPage : currentPage * this.insightsPerPage;
    }

    this.$scope.startIndex = startIndex;
    this.$scope.endIndex = endIndex;
  };
}
